<script>
import Layout from '@layouts/adminLayout.vue'
import { authComputed, personnelComputed } from '@state/helpers'
import LoaderIcon from '@components/loader-icon.vue'
import Tooltip from '@components/tooltip.vue'
import MottoBreadcrumbs from '@components/motto-breadcrumbs.vue'
import FormItemBirthDay from '@components/form-item-birth-day.vue'
import PersonnelFormItemCheckbox from '@components/personnel/personnel-form-item-checkbox.vue'
import PersonnelFormItemLicenseStates from '@components/personnel/personnel-form-license-states.vue'
import PersonnelFormItemGroup from '@components/personnel/personnel-form-item-group.vue'
import LoInstructionsImage from '@components/lo-instructions-image.vue'
import GrayHeader from '@components/gray-header.vue'
import router from '@router'
import {
  getEmailOptions,
  getMottoRoles,
  getUserById,
  getUserDropdownOptions,
  postUser,
  putUser,
} from '@src/api/personnelManagement'
import { mottoRolesMixin } from '@src/mixins/mottoRolesMixin'
import _ from 'lodash'
import { transform as transformUser } from '@src/api/transformers/userTransformer'
import FormItem from '@components/form-item.vue'

export default {
  page: {
    title: 'Personnel Management User Form',
    meta: [
      {
        name: 'description',
        content: 'The Personnel Management User Form page.',
      },
    ],
  },
  components: {
    Layout,
    LoaderIcon,
    MottoBreadcrumbs,
    Tooltip,
    LoInstructionsImage,
    GrayHeader,
    FormItemBirthDay,
    FormItem,
    PersonnelFormItemCheckbox,
    PersonnelFormItemLicenseStates,
    PersonnelFormItemGroup,
  },
  mixins: [mottoRolesMixin],
  data() {
    return {
      requestingPhaseTwoAccess: this.$route.meta?.requestPhaseTwoAccess,
      loadingEmailOptions: false,
      emailOptions: [],
      loading: true,
      error: false,
      shouldBlockRedirect: true,
      submitting: false,
      isOriginatingLoans: false,
      shouldDisplayRevertGroupsToast: false,
      originalLoPhase: 0,
      loPhase: 0,
      timezoneOptions: [],
      timezone: '',
      mottoRoles: [],
      mobileSameAsWorkline: [],
      publishToWeb: [],
      currentStep: 1,
      steps: {
        ROLE_SELECTION: 1,
        BASIC_INFO: 2,
        MLO_PHASE_SELECTION: 3,
        COMPLIANCE: 4,
        MOTTO_EMAIL: 5,
      },
      currentStepAnimation: 'form-slide-right',
      user: {
        status: 'inactive',
        phoneMobile: '',
        phoneMobileExt: '',
        phone: '',
        phoneExt: '',
        firstName: '',
        lastName: '',
        secondaryEmail: '',
        secondaryEmailTemp: '',
        secondaryEmailChangedAt: '',
        nmlsNumber: '',
        locationDetails: '',
      },
      roles: [],
      selectedStates: [],
      reviewMode: false,
    }
  },
  computed: {
    ...authComputed,
    ...personnelComputed,
    pageTitle() {
      if (this.requestingPhaseTwoAccess) {
        return `Requesting Phase 2 Access for ${this.user.name}`
      }
      return 'New User'
    },

    getLocationDetailsPlaceholder() {
      if (this.loPhase === 2 && this.roles.some((x) => x.includes('Loan Originator Phase 2'))) {
        return `${this.user.firstName} is an experienced loan originator at Motto Mortgage ${this.selectedPersonnelOffice.tradeName}. ${this.user.firstName} works directly with multiple wholesale lenders to provide solid, competitive loan options to clients while delivering exceptional customer service and greater transparency.`
      }

      return ''
    },

    getOfficeLicensesAreas() {
      return _.orderBy(this.selectedPersonnelOffice.licenses.map((license) => license.state))
    },

    canDecreaseStep() {
      if (this.requestingPhaseTwoAccess) {
        return this.currentStep > this.steps.MLO_PHASE_SELECTION
      }

      return this.currentStep !== this.steps.ROLE_SELECTION
    },

    canIncreaseStep() {
      if (this.requestingPhaseTwoAccess) {
        return this.currentStep < this.steps.COMPLIANCE
      }

      return this.currentStep !== this.steps.MOTTO_EMAIL
    },

    isFinalStep() {
      if (this.requestingPhaseTwoAccess) {
        return this.currentStep === this.steps.COMPLIANCE
      }

      return this.currentStep === 5
    },

    isMobileNumberSameAsWorkline() {
      return this.mobileSameAsWorkline[0] === 'true'
    },

    isMobileNumberValid() {
      if (this.isMobileNumberSameAsWorkline) {
        return this.user.phone && this.user.phone.length === 12
      }

      return this.user.phoneMobile && this.user.phoneMobile.length === 12
    },

    isLoPhaseTwo() {
      return this.loPhase === 2 && this.isOriginatingLoans
    },

    isThirdParty() {
      return this.roles.includes('Third-party Processor')
    },

    getTimeZoneOptions() {
      return _.values(this.timezoneOptions)
    },

    formStepTitle() {
      if (
        this.currentStep === this.steps.MLO_PHASE_SELECTION &&
        this.isOriginatingLoans &&
        !this.requestingPhaseTwoAccess
      ) {
        return `Select if you would like Phase 1 or Phase 2 access for your new ${this.selectedPersonnelOffice?.tradeName} LO.`
      }

      if (this.currentStep === this.steps.COMPLIANCE && this.isOriginatingLoans && !this.requestingPhaseTwoAccess) {
        return `Next, complete the below compliance information to request Phase 2 access.`
      }

      if (this.currentStep === this.steps.ROLE_SELECTION || this.currentStep === this.steps.MLO_PHASE_SELECTION) {
        return this.requestingPhaseTwoAccess
          ? `Please confirm your LO meets the below requirements for Phase 2 access.`
          : `Let’s start by assigning your new ${this.selectedPersonnelOffice?.tradeName} team member their current role(s).`
      }

      if (this.currentStep === this.steps.BASIC_INFO) {
        return `Now let's fill in some basic information about your new team member.`
      }

      if (this.currentStep === this.steps.COMPLIANCE && this.requestingPhaseTwoAccess) {
        return `Next, complete the below compliance information to request Phase 2 access. `
      }

      if (this.currentStep === this.steps.MOTTO_EMAIL) {
        return `Finally, assign your new team member a Motto and Personal Email.  The activation email for their account will go to their Personal Email.`
      }

      return ''
    },
  },

  watch: {
    roles(newValue, oldValue) {
      this.isOriginatingLoans = this.roles.some((x) => x.includes('Loan Originator'))
      this.loPhase = this.roles.some((x) => x.includes('Loan Originator Phase 2')) ? 2 : 1

      if (this.roles.includes('Third-party Processor') && !oldValue.includes('Third-party Processor')) {
        this.roles = ['Third-party Processor']
      }

      if (this.roles.length > 1 && this.roles.includes('Third-party Processor')) {
        this.roles = this.roles.filter((role) => !role.includes('Third-party Processor'))
      }
    },
    loPhase(newValue) {
      if (newValue === 1 && this.roles.some((x) => x.includes('Loan Originator Phase 2'))) {
        this.roles = [
          ...this.roles.filter((role) => !role.includes('Loan Originator Phase 2')),
          'Loan Originator Phase 1',
        ]
      }

      if (newValue === 2 && this.roles.some((x) => x.includes('Loan Originator Phase 1'))) {
        this.roles = [
          ...this.roles.filter((role) => !role.includes('Loan Originator Phase 1')),
          'Loan Originator Phase 2',
        ]
      }
    },
  },
  created() {
    if (!this.canManagePersonnel) {
      router.push({ name: 'home' })
    }
    this.loadEmailOptionsDebounce = _.debounce(this.loadEmailOptions, 200)
    this.loadUserForm()
  },
  mounted() {
    if (!this.selectedPersonnelOffice.id) {
      this.shouldBlockRedirect = false
      router.push({ name: 'myInfo' })
    }
  },
  methods: {
    async loadUserForm() {
      try {
        const {
          data: { data: mottoRoles },
        } = await getMottoRoles()
        const {
          data: {
            data: { timezone: timezoneData },
          },
        } = await getUserDropdownOptions()
        this.mottoMembershipAccount = mottoRoles.filter((role) => role.value === 'motto_membership')[0]
        this.mottoAccessOnlyAccount = mottoRoles.filter((role) => role.value === 'access_only')[0]
        this.mottoRoles = this.mottoMembershipAccount.groups
        this.thirdPartyProcessorRole = _.chain(mottoRoles)
          .find({ value: 'access_only' })
          .get('groups')
          .find({ value: 'third_party_processor' })
          .value()
        this.timezoneOptions = timezoneData
        if (this.requestingPhaseTwoAccess) {
          this.shouldDisplayRevertGroupsToast = this.$route.params.isOnlyPhaseTwoChecked
          const { data: user } = await getUserById({ id: this.$route.params.id })
          this.user = transformUser(user)
          this.selectedStates = this.user.licenses
          this.roles = _(this.user.groups)
            .orderBy('priority', 'desc')
            .map((group) => {
              if (group.label === 'Loan Processor') {
                return 'In-house Processor'
              }

              if (group.label === 'Loan Originator' && parseInt(user.loPhase) === 2) {
                return 'Loan Originator Phase 2'
              }

              if (group.label === 'Loan Originator') {
                return 'Loan Originator Phase 1'
              }

              return group.label
            })
            .value()
          this.roles = this.roles.some((role) => role === 'Loan Originator Phase 1')
            ? this.roles
            : ['Loan Originator Phase 1', ...this.roles]
          this.isOriginatingLoans = true
          this.originalLoPhase = user.loPhase
          this.loPhase = 0
          this.currentStep = this.steps.MLO_PHASE_SELECTION
        }
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    async loadEmailOptions() {
      try {
        if (!this.user.firstName || !this.user.lastName) {
          return
        }
        this.loadingEmailOptions = true
        const {
          data: { data: emailOptions },
        } = await getEmailOptions({ userDetails: this.user })
        this.emailOptions = emailOptions
        this.user.primaryEmail = emailOptions[0]
      } catch (err) {
      } finally {
        this.loadingEmailOptions = false
      }
    },
    hasDescriptionChanged() {
      return this.$refs.validationObserver?.fields?.Description?.dirty
    },

    isFormValid(currentStep) {
      if (currentStep === this.steps.MLO_PHASE_SELECTION) {
        if (this.roles.some((x) => x.includes('Loan Originator Phase 2'))) {
          return this.loPhase === 2
        }

        return !this.requestingPhaseTwoAccess
      }

      if (currentStep === this.steps.ROLE_SELECTION) {
        return !!this.roles.length
      }

      return this.$refs[`formStep${currentStep}`] ? this.$refs[`formStep${currentStep}`].flags.valid : false
    },

    async enterReviewMode() {
      window.scrollTo(0, 200)
      this.reviewMode = true
    },

    getSelectedGroups() {
      let groups = []

      if (this.isThirdParty) {
        return [this.thirdPartyProcessorRole]
      }

      if (this.isOriginatingLoans) {
        groups = this.mottoRoles.filter((group) => group.label === 'Loan Originator')
      }

      if (this.roles.includes('In-house Processor')) {
        groups = [...groups, ...this.mottoRoles.filter((group) => group.label === 'Loan Processor')]
      }

      return [...groups, ...this.mottoRoles.filter((group) => this.roles.includes(group.label))]
    },

    async submitForm() {
      if (this.submitting) {
        return
      }

      this.submitting = true

      if (this.requestingPhaseTwoAccess) {
        await this.requestPhaseTwoAccess()
        return
      }

      const userOffice = {
        ...this.selectedPersonnelOffice.apiOfficeObject,
        primary: true,
      }

      const userDetails = {
        ...this.user,
        phoneMobile: this.isMobileNumberSameAsWorkline ? this.user.phone : this.user.phoneMobile,
        phoneMobileExt: this.isMobileNumberSameAsWorkline ? this.user.phoneExt : this.user.phoneMobileExt,
        account: this.isThirdParty ? this.mottoAccessOnlyAccount : this.mottoMembershipAccount,
        groups: this.getSelectedGroups(),
        offices: [userOffice],
        licenses: this.selectedStates,
        requestedLoPhase: this.loPhase,
        isOriginatingLoans: this.isOriginatingLoans,
        timeZone: _.findKey(this.timezoneOptions, (x) => x === this.timezone),
        locationDetails: this.user.locationDetails,
        publishToWeb: !!this.publishToWeb[0],
      }

      try {
        await postUser({ userDetails })
        this.$root.$bvToast.toast('User created successfully. Activation email sent.', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-success',
        })

        this.shouldBlockRedirect = false
        await router.push({
          name: 'myInfo',
          params: {
            selectOfficeId: this.selectedPersonnelOffice.id,
          },
        })
      } catch (err) {
        let errorMessage = _.get(err, 'response.data.data', 'Server Error. Please try again in a few moments.')
        if (typeof errorMessage === 'object') {
          errorMessage = _.values(errorMessage)[0]
        }

        if (errorMessage.includes('login: An object with this field already exists in the current organization')) {
          errorMessage = 'This Motto Email is already in use, please use the drop-down to select another option.'
        }
        this.$bvToast.toast(errorMessage, {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.submitting = false
      }
    },

    async requestPhaseTwoAccess() {
      try {
        let userDetails = {
          ...this.user,
          licenses: this.selectedStates,
          group: null,
          groups: this.getSelectedGroups(),
          loPhase: 1,
          requestedLoPhase: 2,
          isOriginatingLoans: this.isOriginatingLoans,
        }

        if (this.$route.params.isOnlyPhaseTwoChecked) {
          userDetails = {
            ...userDetails,
            groups: this.mottoRoles.filter((group) => {
              if (group.label === 'Loan Originator') {
                return true
              }
            }),
          }
        }

        await this.$bvModal
          .msgBoxConfirm(
            `This request will be sent to the Motto Product Support team to review.  Do you want to continue?`,
            {
              modalClass: 'confirmation-modal',
              title: 'Confirm',
              okTitle: 'Continue',
              cancelTitle: 'No',
              centered: true,
            }
          )
          .then(async (confirmation) => {
            if (confirmation) {
              await this.handlePutUser({
                userId: this.user.id,
                userDetails,
              })
            }
          })
      } catch (e) {
      } finally {
        this.submitting = false
      }
    },

    async handlePutUser({ userId, userDetails }) {
      await putUser({
        userId,
        userDetails,
      })

      const message =
        'LO Phase 2 request has been submitted and is currently under review by the Motto Product Support team.  Once your submission has been approved, you will receive an email confirming the LO has production access to their origination system and marketing tools.'
      this.$root.$bvToast.toast(message, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-success',
        autoHideDelay: 10000,
      })

      this.shouldBlockRedirect = false

      await router.push({
        name: 'myInfo',
      })
    },

    confirmRedirect(confirmCallback, declineCallback) {
      this.$bvModal
        .msgBoxConfirm(`Are you sure you wish to leave? Any unsaved changes will be discarded.`, {
          modalClass: 'confirmation-modal confirmation-modal-text-large',
          okTitle: 'Continue',
          cancelTitle: 'Cancel',
          centered: true,
        })
        .then(async (confirmation) => {
          if (confirmation) {
            if (this.shouldDisplayRevertGroupsToast) {
              this.$root.$bvToast.toast(
                `The LO Phase 2 request was not completed.  The user’s prior group(s) have been restored.`,
                {
                  toaster: 'b-toaster-bottom-center',
                  toastClass: 'toast-primary-blue',
                }
              )
            }
            confirmCallback()
          } else {
            declineCallback()
          }
        })
    },

    cancelForm() {
      router.push({ name: 'myInfo' })
    },

    decreaseStep() {
      this.currentStepAnimation = 'form-slide-left'
      this.$nextTick(() => {
        if (this.currentStep === this.steps.MOTTO_EMAIL && !this.isOriginatingLoans) {
          this.currentStep = this.steps.BASIC_INFO
          return
        }

        if (this.currentStep === this.steps.MOTTO_EMAIL && !this.isLoPhaseTwo) {
          this.currentStep = this.steps.BASIC_INFO
          return
        }

        this.currentStep = Math.max(1, this.currentStep - 1)
      })
    },
    increaseStep() {
      this.currentStepAnimation = 'form-slide-right'
      this.$nextTick(() => {
        if (this.currentStep === this.steps.BASIC_INFO && !this.isOriginatingLoans) {
          this.currentStep = this.steps.MOTTO_EMAIL
          return
        }

        if (this.currentStep === this.steps.BASIC_INFO && !this.isLoPhaseTwo) {
          this.currentStep = this.steps.MOTTO_EMAIL
          return
        }

        if (this.currentStep === this.steps.MLO_PHASE_SELECTION && !this.isLoPhaseTwo) {
          this.currentStep = this.steps.MOTTO_EMAIL
          return
        }

        this.currentStep = Math.min(5, this.currentStep + 1)
      })
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.shouldBlockRedirect && this.$store.getters['auth/loggedIn']) {
      this.confirmRedirect(
        () => next(),
        () => next(false)
      )
    } else {
      next()
    }
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon v-if="loading || error" :error="error"></LoaderIcon>
    <div v-else>
      <GrayHeader>
        <MottoBreadcrumbs
          :items="[
            {
              label: selectedPersonnelOffice.tradeName,
              routeName: 'myInfo',
              routeParams: { selectOfficeId: selectedPersonnelOffice.id },
            },
          ]"
          :back-route="'myInfo'"
          :back-route-params="{ selectOfficeId: selectedPersonnelOffice.id }" />
        <BaseTitle :class="$style.title" class="color-primary-blue">{{ pageTitle }}</BaseTitle>
      </GrayHeader>
      <div :class="$style.formStemTitleContainer">
        <h3 :class="$style.formStepTitle">{{ formStepTitle }}</h3>
      </div>
      <h3 :class="$style.formStepTitleMobile">{{ formStepTitle }}</h3>
      <div :class="$style.formStepsContainer">
        <Transition :name="currentStepAnimation">
          <div v-show="currentStep === steps.ROLE_SELECTION">
            <ValidationObserver ref="formStep1" immediate>
              <PersonnelFormItemGroup :class="$style.groupMargin">
                <PersonnelFormItemCheckbox
                  label="Office Employee"
                  :edit-mode="!reviewMode"
                  :value.sync="roles"
                  :checkbox-sub-labels="roleOptionsSubLabels"
                  multi-select
                  :disabled="isThirdParty"
                  :edit-checkbox-values="roleOptions"
                  :exclusive-values="[
                    ['LOA - Licensed', 'LOA - Unlicensed'],
                    ['Loan Originator Phase 1', 'Loan Originator Phase 2'],
                  ]"
                  placeholder="Select...">
                  <template v-slot:label>
                    <p class="button-text bold mb-3">Choose all that apply: </p>
                  </template>
                </PersonnelFormItemCheckbox>
              </PersonnelFormItemGroup>
              <PersonnelFormItemGroup :class="$style.groupMargin">
                <PersonnelFormItemCheckbox
                  label="Contractor"
                  :edit-mode="!reviewMode"
                  :value.sync="roles"
                  :checkbox-sub-labels="roleOptionsSubLabels"
                  multi-select
                  :edit-checkbox-values="thirdPartyRoleOptions"
                  placeholder="Select...">
                  <template v-slot:label>
                    <p class="button-text mb-3">Contractor: </p>
                  </template>
                </PersonnelFormItemCheckbox>
              </PersonnelFormItemGroup>
            </ValidationObserver>
          </div>
        </Transition>
        <Transition :name="currentStepAnimation">
          <div v-show="currentStep === steps.MLO_PHASE_SELECTION">
            <div class="mb-3">
              <h3 class="color-secondary-red">Before selecting Yes (Phase 2 : Full Access):</h3>
              <p class="button-text">
                Please visit the Consumer Access NMLS website at
                <a href="https://www.nmlsconsumeraccess.org" target="_blank">https://www.nmlsconsumeraccess.org</a> to
                ensure the LO is Authorized to Represent your office and has an active state license. Please refer to
                the screenshot below.
              </p>
            </div>
            <LoInstructionsImage />
            <div :class="$style.formGroup">
              <h4 class="color-primary-blue mt-2 mb-3 font-weight-bold"
                >Does the Consumer Access NMLS website display all of the highlighted information above?</h4
              >
              <div :class="$style.buttonsContainer">
                <BaseRadioButton
                  v-if="!requestingPhaseTwoAccess"
                  v-model="loPhase"
                  label="No (Phase 1: Training Access)"
                  :value="1"
                  name="loPhase" />
                <BaseRadioButton v-model="loPhase" label="Yes (Phase 2: Full Access)" :value="2" name="loPhase" />
              </div>
            </div>
          </div>
        </Transition>
        <Transition :name="currentStepAnimation">
          <div v-show="currentStep === steps.COMPLIANCE">
            <ValidationObserver ref="formStep4" immediate>
              <PersonnelFormItemGroup v-if="isLoPhaseTwo" :class="$style.groupMargin">
                <FormItem
                  label="NMLS #"
                  :value.sync="user.nmlsNumber"
                  :edit-mode="!reviewMode"
                  placeholder="NMLS #"
                  :mask="'#*'"
                  rules="required" />
              </PersonnelFormItemGroup>
              <PersonnelFormItemGroup v-if="isLoPhaseTwo" :class="$style.groupMargin">
                <FormItem
                  label="ORM Location Description"
                  :placeholder="getLocationDetailsPlaceholder"
                  :value.sync="user.locationDetails"
                  sub-label="The following will be used to fill in any website listing descriptions on websites such as Google, Bing, Yelp, and many more. If you would like something else please enter your own description below:"
                  :edit-mode="!reviewMode"
                  textarea
                  rules="max:750" />
              </PersonnelFormItemGroup>
              <PersonnelFormItemGroup v-if="isLoPhaseTwo" :class="$style.groupMargin">
                <PersonnelFormItemLicenseStates
                  :label="'State(s) of Licensure'"
                  :sub-label="'Choose all that apply'"
                  :edit-mode="!reviewMode"
                  :value.sync="selectedStates"
                  :edit-select-values="getOfficeLicensesAreas"
                  :disabled="!getOfficeLicensesAreas.length"
                  :placeholder="getOfficeLicensesAreas.length ? 'Select...' : 'No states are eligible for this user'"
                  rules="required" />
              </PersonnelFormItemGroup>
              <PersonnelFormItemGroup v-if="isLoPhaseTwo" :class="$style.groupMargin">
                <FormItem
                  label="Notes"
                  :value.sync="user.notes"
                  :edit-mode="!reviewMode"
                  placeholder="Use this field to describe any special considerations related to this request" />
              </PersonnelFormItemGroup>
            </ValidationObserver>
          </div>
        </Transition>
        <Transition :name="currentStepAnimation">
          <div v-show="currentStep === steps.BASIC_INFO">
            <ValidationObserver ref="formStep2" immediate>
              <PersonnelFormItemGroup :class="$style.groupMargin">
                <FormItem
                  label="First Name"
                  :value.sync="user.firstName"
                  :edit-mode="!reviewMode"
                  placeholder="First Name"
                  rules="required|max:70"
                  :character-limit-options="{ showOnlyError: true }"
                  @update="loadEmailOptionsDebounce"></FormItem>
                <FormItem
                  label="Last Name"
                  :value.sync="user.lastName"
                  :edit-mode="!reviewMode"
                  placeholder="Last Name"
                  rules="required|max:70"
                  :character-limit-options="{ showOnlyError: true }"
                  @update="loadEmailOptionsDebounce"></FormItem>
                <FormItem
                  label="Job Title"
                  :value.sync="user.jobTitle"
                  :edit-mode="!reviewMode"
                  rules="max:60"
                  :character-limit-options="{ showOnlyError: true }"
                  placeholder="Job Title"></FormItem>
              </PersonnelFormItemGroup>
              <PersonnelFormItemGroup :class="$style.groupMargin">
                <FormItem
                  label="Work line"
                  :value.sync="user.phone"
                  :mask="'###-###-####'"
                  :edit-mode="!reviewMode"
                  rules="required|validPhoneNumber"
                  placeholder="xxx-xxx-xxxx"
                  :style="[reviewMode ? { 'flex-grow': '1' } : { 'flex-grow': '5' }]" />
                <FormItem
                  label="Ext."
                  :value.sync="user.phoneExt"
                  placeholder="Work line Ext."
                  :mask="'######'"
                  :edit-mode="!reviewMode"
                  name="Work line Ext." />
              </PersonnelFormItemGroup>
              <PersonnelFormItemGroup class="mb-3">
                <FormItem
                  label="Mobile Number"
                  :value.sync="isMobileNumberSameAsWorkline ? user.phone : user.phoneMobile"
                  :mask="'###-###-####'"
                  :disabled="isMobileNumberSameAsWorkline"
                  :edit-mode="!reviewMode"
                  :rules="'required|validPhoneNumber'"
                  placeholder="xxx-xxx-xxxx"
                  :style="[reviewMode ? { 'flex-grow': '1' } : { 'flex-grow': '5' }]" />
                <FormItem
                  label="Ext."
                  :value.sync="isMobileNumberSameAsWorkline ? user.phoneExt : user.phoneMobileExt"
                  :disabled="isMobileNumberSameAsWorkline"
                  placeholder="Mobile Ext."
                  :edit-mode="!reviewMode"
                  name="Mobile Number Ext."
                  :mask="'######'" />
              </PersonnelFormItemGroup>
              <div :class="[$style.groupMargin, $style.mobileNumberCheckboxes]">
                <BaseCheckbox
                  v-if="!reviewMode"
                  v-model="mobileSameAsWorkline"
                  class="mr-3"
                  label="Same as Work line"
                  input-value="true" />
                <div class="d-flex align-items-center">
                  <BaseCheckbox
                    v-model="publishToWeb"
                    class="mr-2"
                    :class="[isMobileNumberValid ? '' : 'pe-none']"
                    label="Publish to Web"
                    input-value="1" />
                  <Tooltip :target="'publish-to-web'" :icon-absolute-position="false" placement="top" theme="blue">
                    Checking this box will publish your Mobile number on the web.
                  </Tooltip>
                </div>
              </div>
              <PersonnelFormItemGroup :class="$style.groupMargin">
                <FormItem
                  label="Time Zone"
                  :edit-mode="!reviewMode"
                  :value.sync="timezone"
                  :rules="'required'"
                  :edit-select-values="getTimeZoneOptions"
                  placeholder="Select..." />
                <FormItemBirthDay
                  :edit-mode="!reviewMode"
                  :birth-day.sync="user.birthDay"
                  :birth-month.sync="user.birthMonth"></FormItemBirthDay>
              </PersonnelFormItemGroup>
            </ValidationObserver>
          </div>
        </Transition>
        <Transition :name="currentStepAnimation">
          <div v-show="currentStep === steps.MOTTO_EMAIL">
            <ValidationObserver ref="formStep5" immediate>
              <PersonnelFormItemGroup v-if="!isThirdParty" :class="$style.groupMargin">
                <LoaderIcon v-if="loadingEmailOptions"></LoaderIcon>
                <FormItem
                  v-if="!loadingEmailOptions"
                  label="Motto Email"
                  :edit-mode="!reviewMode"
                  :rules="isThirdParty ? '' : 'required'"
                  :edit-select-values="emailOptions"
                  placeholder="Select..."
                  :value.sync="user.primaryEmail" />
              </PersonnelFormItemGroup>
              <PersonnelFormItemGroup :class="$style.groupMargin">
                <FormItem
                  label="Personal Email"
                  :value.sync="user.secondaryEmail"
                  sub-label="This should NOT be a @mottomortgage.com email. It should be the personal email for the new user to receive their MottoCenter activation email to."
                  :edit-mode="!reviewMode"
                  placeholder="Personal Email"
                  :character-limit-options="{ showOnlyError: true }"
                  rules="required|email|max:75" />
              </PersonnelFormItemGroup>
            </ValidationObserver>
          </div>
        </Transition>
      </div>
      <div :class="$style.formStepsButtons">
        <BaseButton v-if="canDecreaseStep" :disabled="!canDecreaseStep" @click="decreaseStep">Back</BaseButton>
        <div v-if="!canDecreaseStep" />
        <div class="d-flex">
          <BaseButton class="mr-5" theme="white" @click="cancelForm">Cancel</BaseButton>
          <BaseButton
            v-if="!isFinalStep"
            :disabled="!isFormValid(currentStep) || !canIncreaseStep"
            @click="increaseStep">
            Next
          </BaseButton>
          <BaseButton v-if="isFinalStep" theme="green" :disabled="!isFormValid(currentStep)" @click="submitForm">
            <template v-if="submitting">
              <BaseIcon name="spinner" spin />
            </template>
            <template v-else>Submit</template>
          </BaseButton>
        </div>
      </div>
      <div :class="$style.formStepsButtonsMobile">
        <BaseButton v-if="canDecreaseStep" :disabled="!canDecreaseStep" @click="decreaseStep">Back</BaseButton>
        <div v-if="!canDecreaseStep" />
        <div :class="$style.cancelButtonContainerMobile" class="d-flex">
          <BaseButton theme="white" @click="cancelForm">Cancel</BaseButton>
          <BaseButton
            v-if="!isFinalStep"
            :disabled="!isFormValid(currentStep) || !canIncreaseStep"
            @click="increaseStep">
            Next
          </BaseButton>
          <BaseButton v-if="isFinalStep" theme="green" :disabled="!isFormValid(currentStep)" @click="submitForm">
            <template v-if="submitting">
              <BaseIcon name="spinner" spin />
            </template>
            <template v-else>Submit</template>
          </BaseButton>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.formStepsContainer {
  @media (max-width: $lg-breakpoint) {
    padding: unset;
    border: unset;
  }

  position: relative;
  padding: 20px 20px;
  background-color: $white;
  border: 2px solid $light-gray;
  border-radius: 8px;
}

.title {
  margin-top: 33px;
  margin-bottom: 42px;
}

.buttonsContainer {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;

    > div:first-child {
      margin-bottom: 10px;
    }
  }

  display: flex;

  > * {
    margin-right: 16px;
  }

  p {
    margin-bottom: 0 !important;
  }
}

.formGroup {
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;

  p {
    margin-bottom: 16px;
  }
}

.mobileNumberCheckboxes {
  @media (max-width: $sm-breakpoint) {
    flex-wrap: wrap;
    align-items: start;
    gap: 10px;
  }

  display: flex;
  align-items: center;
}

.groupMargin {
  @media (max-width: $lg-breakpoint) {
    margin-bottom: 0;
  }

  margin-bottom: 32px;
}

.formStemTitleContainer {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  margin-bottom: 20px;
  text-align: center;
}

.bottomButtonsContainer {
  @media (max-width: $lg-breakpoint) {
    margin-top: 32px;
  }

  display: flex;
  justify-content: center;
  margin-top: 5px;

  button:first-child {
    margin-right: 24px;
  }
}

p.formGroupSubLabel {
  margin: -12px 0 6px;
  color: $secondary-light-blue;
}

.formStepsButtons {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;

  button:only-child {
    margin-left: auto;
  }

  button {
    width: 100px;
  }
}

.formStepsButtonsMobile {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    margin-top: 20px;
  }

  display: none;
  justify-content: space-between;

  button:only-child {
    margin-left: auto;
  }

  button {
    width: 100px;
  }
}

.cancelButtonContainerMobile {
  > button:first-child {
    margin-right: 5px;
  }
}

.formStepTitle {
  margin: 0 30px;
  text-align: center;
}

.formStepTitleMobile {
  @media (max-width: $lg-breakpoint) {
    display: block;
  }

  display: none;
  margin-bottom: 30px;
  text-align: center;
}
</style>
